// "On location": which spots are shown for a time filter on the public map. Pure functions, in the
// browser's own time zone (a spot "today" means today where the visitor is).

export const SPOT_FILTERS = ['all', 'today', 'weekend', 'later'];

const DAY_MS = 24 * 60 * 60 * 1000;

const startOfDay = date => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

/** Saturday 00:00 up to Monday 00:00 of the current weekend (when it is one) or the next one. */
export const weekendRange = (now = new Date()) => {
  const today = startOfDay(now);
  const day = today.getDay(); // 0 = Sunday ... 6 = Saturday
  const saturday = day === 6 ? today : day === 0 ? addDays(today, -1) : addDays(today, 6 - day);
  return [saturday, addDays(saturday, 2)];
};

const overlaps = (spot, from, to) => new Date(spot.start) < to && new Date(spot.end) > from;

/**
 * Spots for a filter: 'today' and 'weekend' mean "at least partly in that time", 'later' means
 * "starts after this weekend", 'all' keeps everything. Spots that are over are never shown.
 */
export const filterSpots = (spots, filter, now = new Date()) => {
  const running = (spots || []).filter(spot => new Date(spot.end) > now);
  const today = startOfDay(now);
  const [, weekendEnd] = weekendRange(now);
  switch (filter) {
    case 'today':
      return running.filter(spot => overlaps(spot, today, new Date(today.getTime() + DAY_MS)));
    case 'weekend':
      return running.filter(spot => overlaps(spot, ...weekendRange(now)));
    case 'later':
      return running.filter(spot => new Date(spot.start) >= weekendEnd);
    default:
      return running;
  }
};

/**
 * The spot to point out on a photographer's profile: the one running now, else the next one to
 * start. `running` tells which of the two it is. Returns null when she has none.
 */
export const spotForProfile = (spots, userId, now = new Date()) => {
  const own = (spots || [])
    .filter(spot => spot.authorId === userId && new Date(spot.end) > now)
    .sort((a, b) => new Date(a.start) - new Date(b.start));
  if (own.length === 0) {
    return null;
  }
  const current = own.find(spot => new Date(spot.start) <= now);
  return current ? { spot: current, running: true } : { spot: own[0], running: false };
};

const EARTH_RADIUS_KM = 6371;
const toRad = deg => (deg * Math.PI) / 180;

/** Straight-line distance in kilometres between two { lat, lng } points (haversine). */
export const distanceKm = (a, b) => {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
};

/**
 * The spots with a `distanceKm` from `origin` ({ lat, lng }), nearest first. Spots without a usable
 * position go last. Positions are rounded to ~300 m on the server, so the distance is approximate.
 */
export const withDistance = (spots, origin) => {
  const usable = p => p && Number.isFinite(p.lat) && Number.isFinite(p.lng);
  if (!usable(origin)) {
    return spots || [];
  }
  return (spots || [])
    .map(spot => ({ ...spot, distanceKm: usable(spot) ? distanceKm(origin, spot) : null }))
    .sort((a, b) => {
      if (a.distanceKm === null) return b.distanceKm === null ? 0 : 1;
      if (b.distanceKm === null) return -1;
      return a.distanceKm - b.distanceKm;
    });
};
